// lib/resultFeedback.ts
//
// Read whether a user's chat message is clearly approving or rejecting the result
// they were just shown — so the Studio Cortex reward can learn from what the user
// SAYS, not only from an explicit favourite click. Deliberately conservative
// (returns null when in doubt), mirroring the house heuristic-classifier style
// (lib/analystModelRouter.ts): a wrong signal is worse than a missed one.
//
// It intentionally does NOT treat a bare "no" or a refinement request ("make it
// bigger", "change the sign") as a rejection — that is normal iteration, not the
// user rejecting the result. Only explicit dissatisfaction counts.

type ResultFeedback = 'approve' | 'reject' | null;

// Clear praise of the result. Multi-word where possible to avoid false hits.
const APPROVE = [
  'perfect', 'love it', 'love this', 'looks great', 'looks good', 'looks perfect',
  'looks amazing', "that's it", 'thats it', "that's perfect", 'exactly what', 'exactly right',
  'nailed it', 'ship it', 'keep it', 'keep this', 'amazing', 'gorgeous', 'beautiful',
  'great job', 'well done', 'that works', 'works great', 'works well', 'i like it', 'like it a lot',
  'this is great', 'this is perfect',
];

// Explicit rejection of the result (NOT a refinement request).
const REJECT = [
  "that's wrong", 'thats wrong', 'not right', 'not quite', 'not what i', "that's not it",
  'thats not it', "that's not what", 'try again', 'redo it', 'do it again', "isn't right",
  "doesn't work", 'not good', 'hate it', "don't like it", 'dont like it', 'this is wrong',
  'start over', 'completely off',
];

const NEGATION = /\b(no|not|nope|never|isn'?t|aren'?t|wasn'?t|don'?t|doesn'?t|didn'?t|won'?t|can'?t|n't)\b/;

/**
 * Classify a user's message as approving / rejecting the last result, or null when
 * it is neither (a new request, small talk, or anything ambiguous).
 */
export function classifyResultFeedback(text: string): ResultFeedback {
  if (typeof text !== 'string') return null;
  const s = text.toLowerCase().trim();
  if (!s || s.length > 400) return null; // long messages are almost always fresh instructions

  if (REJECT.some((p) => s.includes(p))) return 'reject';
  // Approval only when there's clear praise AND no negation anywhere (guards against
  // "not perfect", "no, that looks good but…" → treat as ambiguous, not approval).
  if (APPROVE.some((p) => s.includes(p)) && !NEGATION.test(s)) return 'approve';
  return null;
}
